import { DiveSite } from '../types/DiveSite';

export interface Region {
  slug: string;
  region: string;
  country: string;
  sites: DiveSite[];
}

const slugify = (value: string): string =>
  value.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const regionSlug = (region: string, country: string): string => slugify(`${region}-${country}`);

/** The slug the server's region index uses for a country (the wider tier on a region page). */
export const countrySlug = (country: string): string => slugify(country);

/** Groups the dive-site catalog into regions — the unit a community page is built around. */
export const buildRegions = (sites: DiveSite[]): Region[] => {
  const byKey = new Map<string, Region>();
  for (const site of sites) {
    if (!site.region?.trim()) continue;
    const slug = regionSlug(site.region, site.country ?? '');
    const existing = byKey.get(slug);
    if (existing) existing.sites.push(site);
    else byKey.set(slug, { slug, region: site.region.trim(), country: (site.country ?? '').trim(), sites: [site] });
  }
  return Array.from(byKey.values()).sort((a, b) => b.sites.length - a.sites.length || a.region.localeCompare(b.region));
};

const normalize = (value: string): string => value.trim().toLowerCase();

export interface HomeSiteRef {
  homeSiteId?: string | null;
  homeSiteName?: string | null;
}

const siteMatches = (site: DiveSite, ref: HomeSiteRef): boolean => {
  if (ref.homeSiteId && (site.id === ref.homeSiteId || site.siteId === ref.homeSiteId)) return true;
  if (!ref.homeSiteName?.trim()) return false;
  const target = normalize(ref.homeSiteName);
  return normalize(site.name) === target || (site.aliases ?? []).some(a => normalize(a) === target);
};

/** The catalog site a diver's home site refers to — by stored ID when present, else by name or alias. */
export const siteForHomeSite = (ref: HomeSiteRef, sites: DiveSite[]): DiveSite | null =>
  sites.find(s => siteMatches(s, ref)) ?? null;

/** The catalog ID to store for a home-site name typed or picked in a profile field, or undefined for free text. */
export const resolveHomeSiteId = (name: string, sites: DiveSite[]): string | undefined =>
  siteForHomeSite({ homeSiteName: name }, sites)?.id;

/** Which region a diver's home site belongs to. */
export const regionForHomeSite = (ref: HomeSiteRef, regions: Region[]): Region | null =>
  regions.find(r => r.sites.some(s => siteMatches(s, ref))) ?? null;

/** True when two regions sit in the same, non-empty country. */
export const sameCountry = (a: Pick<Region, 'country'>, b: Pick<Region, 'country'>): boolean =>
  !!a.country && normalize(a.country) === normalize(b.country);

export const regionLabel = (r: Pick<Region, 'region' | 'country'>): string =>
  r.country && r.country !== r.region ? `${r.region}, ${r.country}` : r.region;

export interface RegionMembershipRef extends HomeSiteRef {
  joinedRegionSlugs?: string[] | null;
}

/** A diver whose home site is inside the region — the implicit tier of membership. */
export const isLocalTo = (diver: RegionMembershipRef, region: Region): boolean => regionForHomeSite(diver, [region]) !== null;

/** A diver who explicitly joined the region without living there (a home-region diver is "local", not "joined"). */
export const hasJoined = (diver: RegionMembershipRef, region: Region): boolean =>
  !isLocalTo(diver, region) && (diver.joinedRegionSlugs ?? []).includes(region.slug);

/** Every region a diver belongs to: the home-site region plus any they joined. */
export const membershipsOf = (diver: RegionMembershipRef, regions: Region[]): Region[] => {
  const home = regionForHomeSite(diver, regions);
  const joined = (diver.joinedRegionSlugs ?? [])
    .map(slug => regions.find(r => r.slug === slug))
    .filter((r): r is Region => !!r && r.slug !== home?.slug);
  return home ? [home, ...joined] : joined;
};

export interface Country {
  slug: string;
  name: string;
  regions: Region[];
  siteCount: number;
}

/** Groups regions into countries — the unit of the country page. Regions with no country are left out. */
export const buildCountries = (regions: Region[]): Country[] => {
  const byKey = new Map<string, Country>();
  for (const region of regions) {
    const name = region.country.trim();
    if (!name) continue;
    const slug = countrySlug(name);
    const existing = byKey.get(slug);
    if (existing) { existing.regions.push(region); existing.siteCount += region.sites.length; }
    else byKey.set(slug, { slug, name, regions: [region], siteCount: region.sites.length });
  }
  return Array.from(byKey.values()).sort((a, b) => b.siteCount - a.siteCount || a.name.localeCompare(b.name));
};
